import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Chip from '../Chip';
import FormInput, { FORM_INPUT_SIZE } from '../FormInput';
import Button from '../../legacy/core/Button';
import { useTranslations } from '../../context/TranslationContext';
import { INLINE_CHIP_INPUT_ICON, INLINE_CHIP_INPUT_KEY } from './constants';

/**
 * A single optional text value shown in three states: an "+ Add" link when empty, a chip when
 * set (click to edit, × to clear), and a text field while editing. Enter or blur commits,
 * Escape cancels. Editing state is local unless `editing` / `onEditingChange` are supplied.
 */
const InlineChipInput = ({
    value = '',
    onChange = () => {},
    disabled = false,
    placeholder,
    editing: editingProp,
    onEditingChange = () => {},
    labels = {},
    size = FORM_INPUT_SIZE.SM,
    className,
    id,
    ...rest
}) => {
    const { t } = useTranslations();
    const [editingState, setEditingState] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = useRef(null);
    const committedRef = useRef(false);

    const editing = editingProp !== undefined ? editingProp : editingState;
    const setEditing = (next) => {
        if (editingProp === undefined) setEditingState(next);
        onEditingChange(next);
    };

    useEffect(() => {
        setDraft(value ?? '');
    }, [value]);

    useEffect(() => {
        if (editing) {
            committedRef.current = false;
            inputRef.current?.focus();
        }
    }, [editing]);

    const addLabel = labels.add || t('Add');
    const editLabel = labels.edit || t('Edit');
    const clearLabel = labels.clear || t('Remove');

    const commit = (next) => {
        if (committedRef.current) return;
        committedRef.current = true;
        const trimmed = (next ?? '').trim();
        if (trimmed !== (value ?? '')) onChange(trimmed);
        setEditing(false);
    };

    const cancel = () => {
        committedRef.current = true;
        setDraft(value ?? '');
        setEditing(false);
    };

    const startEditing = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!disabled) setEditing(true);
    };

    const wrapperClass = classNames('ds-inline-chip-input', { 'ds-inline-chip-input--disabled': disabled }, className);

    if (editing && !disabled) {
        return (
            <div className={wrapperClass} {...rest}>
                <FormInput
                    ref={inputRef}
                    id={id}
                    size={size}
                    value={draft}
                    placeholder={placeholder}
                    onChange={(event) => setDraft(event?.target ? event.target.value : event)}
                    onBlur={() => commit(draft)}
                    onKeyDown={(event) => {
                        if (event.key === INLINE_CHIP_INPUT_KEY.ENTER) {
                            event.preventDefault();
                            commit(draft);
                        } else if (event.key === INLINE_CHIP_INPUT_KEY.ESCAPE) {
                            event.preventDefault();
                            cancel();
                        }
                    }}
                    className="ds-inline-chip-input__field"
                />
            </div>
        );
    }

    if (value) {
        return (
            <div className={wrapperClass} {...rest}>
                <Chip
                    className="ds-inline-chip-input__chip"
                    disabled={disabled}
                    value={
                        <button
                            type="button"
                            className="ds-inline-chip-input__chip-button"
                            onClick={startEditing}
                            disabled={disabled}
                            aria-label={`${editLabel}: ${value}`}
                            title={value}
                        >
                            {value}
                        </button>
                    }
                    removeTooltip={disabled ? undefined : clearLabel}
                    removeLabel={`${clearLabel}: ${value}`}
                    onRemove={disabled ? undefined : () => onChange('')}
                />
            </div>
        );
    }

    return (
        <div className={wrapperClass} {...rest}>
            <Button
                className="ds-inline-chip-input__add"
                type="secondary"
                size="xxs"
                onlyIcon
                icon={INLINE_CHIP_INPUT_ICON.ADD}
                altTitle={addLabel}
                aria-label={addLabel}
                disabled={disabled}
                onClick={startEditing}
            />
        </div>
    );
};

InlineChipInput.propTypes = {
    /** Committed text; empty shows the add affordance */
    value: PropTypes.string,
    /** Called with the trimmed text on commit, or `''` when cleared */
    onChange: PropTypes.func,
    /** Blocks editing and clearing */
    disabled: PropTypes.bool,
    /** Placeholder while editing */
    placeholder: PropTypes.string,
    /** Controlled editing state (leave undefined for local state) */
    editing: PropTypes.bool,
    /** Fires with the next editing state */
    onEditingChange: PropTypes.func,
    /** Translated texts: `{ add, edit, clear }` */
    labels: PropTypes.shape({ add: PropTypes.string, edit: PropTypes.string, clear: PropTypes.string }),
    /** FormInput size while editing */
    size: PropTypes.oneOf([...Object.values(FORM_INPUT_SIZE)]),
    /** Extra class on the wrapper */
    className: PropTypes.string,
    /** Id forwarded to the text field */
    id: PropTypes.string,
};

export { INLINE_CHIP_INPUT_ICON, INLINE_CHIP_INPUT_KEY };
export default InlineChipInput;
